import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { UiGrid, UiFlex } from '@open-stack/ui';
import { useCurrency } from '../../../../hooks/currency';
import { useOrderProductOverlay } from '../../../../hooks/order-product-overlay';
import { useHtmlDocument } from '../../../../hooks/html-document';
import { XDrawerBottom } from '../../../x/drawer-bottom/XDrawerBottom';
import { OrderProductVariantProduct } from '../modifier/variant-product/OrderProductVariantProduct';
import { OrderProductModifierGroup } from '../modifier/group/OrderProductModifierGroup';
import { MenuProductCardIcon } from '../../../menu/product/card/icon/MenuProductCardIcon';
import { XInput } from '../../../x/input/XInput';
import { XButtonBottom } from '../../../x/button/bottom/XButtonBottom';
import { XText, XTextType } from '../../../x/text/XText';
import { XCard } from '../../../x/card/XCard';
import { orderByPosition } from '../../../../util/orderByPosition';
import { ProductModifierGroup } from '../../../../XV2';
import { useActiveOrder, ActiveOrderProduct } from '../../../../hooks/active-order';
import { useImmer } from 'use-immer';
import isEmpty from 'lodash/isEmpty';
import { useActiveOrderManager } from '../../../../hooks/useActiveOrderManager';
import { XButton } from '@orderx/lab/ui';

const Wrapper = styled(UiGrid)`
    .button-placeholder {
        width: var(--ui-size-2);
    }

    .out-of-stock {
        text-decoration: line-through;
    }
`;

export function OrderProductFormDialog() {
    const { setIsOverflowHidden } = useHtmlDocument();
    const { menuProduct } = useOrderProductOverlay();

    const isOpen = Boolean(menuProduct);

    useEffect(() => {
        if (isOpen) {
            setIsOverflowHidden(true);
            return () => setIsOverflowHidden(false);
        }
    }, [isOpen]);

    return menuProduct ? <Index /> : null;
}

function Index() {
    const { menuProduct, orderProductId, reset: handleClose } = useOrderProductOverlay();
    const { format } = useCurrency();
    const { product } = menuProduct;
    const { getOrderProductById, updateProduct, removeOrderProductById, createOrderProduct } = useActiveOrder();
    const activeOrderManager = useActiveOrderManager();

    const [orderProduct, setOrderProduct] = useImmer<ActiveOrderProduct>(undefined as any);

    useEffect(() => {
        setOrderProduct(() => getOrderProductById(orderProductId) || createOrderProduct({ menuProduct }));
    }, [orderProductId]);

    const isRequiredModifierGroupsSelected = useMemo(() => {
        if (!orderProduct) {
            return false;
        }

        const requiredIds = new Set();

        for (const productModifierGroup of menuProduct.product.productModifierGroups) {
            if (productModifierGroup.modifierGroup.minAllowed > 0) {
                requiredIds.add(productModifierGroup.modifierGroupId);
            }
        }

        for (const modifierGroupId of Object.keys(orderProduct.modifierGroups || {}).map(Number)) {
            requiredIds.delete(modifierGroupId);
        }

        return requiredIds.size === 0;
    }, [menuProduct, orderProduct]);

    if (!orderProduct) {
        return null;
    }

    const existingOrderProduct = getOrderProductById(orderProductId);
    const isExisting = Boolean(existingOrderProduct);
    const totalCost = activeOrderManager.calculateActiveOrderProductPrice(orderProduct);

    return (
        <XDrawerBottom onClose={handleClose}>
            <Wrapper padding={2}>
                <UiFlex alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <MenuProductCardIcon icon={product.icon} isLarge />

                    <UiFlex gap={1}>
                        {isExisting && (
                            <XButton
                                onClick={() => {
                                    removeOrderProductById(orderProduct.id);
                                    handleClose();
                                }}
                                icon="delete_forever"
                                color="error"
                            />
                        )}

                        <XButton onClick={handleClose} icon="close" variant="outlined" />
                    </UiFlex>
                </UiFlex>

                <UiGrid gap={0}>
                    <XText type={XTextType.TITLE}>{product.name}</XText>
                    <XText type={XTextType.CARD_DESCRIPTION}>{product.description}</XText>
                </UiGrid>

                {orderProduct.menuProduct.product.variantGroup && (
                    <OrderProductVariantProduct
                        orderProduct={orderProduct}
                        onUpdateVariant={(variantGroupProduct) => {
                            setOrderProduct((draft) => {
                                draft.variantGroupProductId = variantGroupProduct.id;
                                draft.variantGroupProduct = variantGroupProduct;
                            });
                        }}
                    />
                )}

                {orderByPosition<ProductModifierGroup>(product.productModifierGroups).map(
                    ({ productId, modifierGroup }) => (
                        <OrderProductModifierGroup
                            orderProduct={orderProduct}
                            modifierGroup={modifierGroup}
                            onSelectModifierGroupItem={(modifierGroupItem) => {
                                const groupId = modifierGroup.id;
                                const itemId = modifierGroupItem.id;
                                const isMultipleAllowed = modifierGroup.maxAllowed > 1;

                                setOrderProduct((draft) => {
                                    const exists = Boolean(draft.modifierGroups?.[groupId]?.[itemId]);

                                    if (exists) {
                                        if (isMultipleAllowed) {
                                            delete draft.modifierGroups[groupId][itemId];

                                            if (isEmpty(draft.modifierGroups[groupId])) {
                                                delete draft.modifierGroups[groupId];
                                            }
                                        } else {
                                            delete draft.modifierGroups[groupId];
                                        }
                                    } else {
                                        draft.modifierGroups[groupId] = draft.modifierGroups[groupId] || {};

                                        if (isMultipleAllowed) {
                                            draft.modifierGroups[groupId][itemId] = modifierGroupItem;
                                        } else {
                                            draft.modifierGroups[groupId] = { [itemId]: modifierGroupItem };
                                        }
                                    }
                                });
                            }}
                            key={`${productId}-${modifierGroup.id}`}
                        />
                    ),
                )}

                {product.isCommentAllowed && (
                    <XCard gap={2} padding={2}>
                        <XText type={XTextType.TITLE}>Any comments?</XText>

                        <XInput
                            value={orderProduct.comment}
                            onChangeValue={(comment) =>
                                setOrderProduct((draft) => {
                                    draft.comment = comment;
                                })
                            }
                            placeholder="Leave a note for the kitchen"
                            clearable
                        />
                    </XCard>
                )}
            </Wrapper>

            <XButtonBottom
                hasPlaceholder
                onClick={() => {
                    updateProduct(orderProduct);
                    handleClose();
                }}
                disabled={!isRequiredModifierGroupsSelected}
                color="success">
                <UiFlex gap={1}>
                    <span>{isExisting ? 'Update' : 'Add to order'}</span>
                    <span>•</span>
                    <span>{format(totalCost)}</span>
                </UiFlex>
            </XButtonBottom>
        </XDrawerBottom>
    );
}
